import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IAsset, IAssetFileQuality } from '~/types/assets-management'
import type { IUserPublic } from '~/types/user'

import AssetDetailsFileManagement from './FileManagement'
import AssetDetailsGeneralInformation from './GeneralInformation'

import EditAssetModal from '../modals/EditModal'

import BackNavigation from '~/components/BackNavigation'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import ViewTitle from '~/components/ViewTitle'
import { assetsManagementKeys, useAssetDetailsQuery, useUpdateAssetMutation } from '~/queries/assets'
import { useUserDetailsQuery } from '~/queries/users'
import { ASSET_STATUS } from '~/types/assets-management'
import { fileToFileQuality } from '~/utils/asset'
import { formatBuilder, isUserId } from '~/utils/builder'
import { displayToast } from '~/utils/toast'
import { formatUser } from '~/utils/user'

import './styles.scss'

const AssetDetail = (): ReactElement => {
  const { assetId } = useParams<{ assetId: string }>()

  const queryClient = useQueryClient()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data: assetData, isLoading } = useAssetDetailsQuery(assetId)

  const { data: createdByData } = useUserDetailsQuery(
    assetData?.created_by,
    !!assetData?.created_by,
    ({ name, picture, profile }: IUserPublic) => {
      const formattedName = isUserId((assetData as IAsset).created_by)
        ? name
        : formatBuilder((assetData as IAsset).created_by)

      return formatUser({
        created_at: (assetData as IAsset).created_at,
        job_title: profile?.job_title,
        name: formattedName,
        picture,
      })
    }
  )

  const { data: updatedByByData } = useUserDetailsQuery(
    assetData?.updated_by,
    !!assetData?.updated_by,
    ({ name, picture, profile }: IUserPublic) => {
      const formattedName = isUserId((assetData as IAsset).updated_by)
        ? name
        : formatBuilder((assetData as IAsset).created_by)

      return formatUser({
        created_at: (assetData as IAsset).updated_at,
        job_title: profile?.job_title,
        name: formattedName,
        picture,
      })
    }
  )

  const { mutateAsync: updateAssetMutation } = useUpdateAssetMutation()

  if (isLoading) {
    return (
      <div className="loader-container">
        <MwMLoader category="general" />
      </div>
    )
  }

  const handleRemoveAllFiles = async () => {
    if (!assetData || !assetData.files) return

    try {
      //eslint-disable-next-line
      const { files, ...assetToUpdate } = assetData

      await updateAssetMutation({ ...assetToUpdate, file_quality_ids: [] })

      queryClient.invalidateQueries({ queryKey: assetsManagementKeys.details(assetId) })

      displayToast('All files have been successfully removed', 'success')
    } catch (e) {
      displayToast('Files could not be removed', 'error')
    }
  }

  const handleRemoveSingleFile = async (fileId: string) => {
    if (!assetData || !assetData.files) return

    try {
      const { files, ...assetToUpdate } = assetData

      const newFileQualityIds: IAssetFileQuality[] = files
        ? files.filter(({ id }) => id !== fileId).map(fileToFileQuality)
        : []

      updateAssetMutation(
        { ...assetToUpdate, file_quality_ids: newFileQualityIds },
        {
          onSettled: () => {
            queryClient.invalidateQueries({ queryKey: assetsManagementKeys.details(assetId) })
          },
        }
      )

      const previousAsset: IAsset | undefined = queryClient.getQueryData(assetsManagementKeys.details(assetId))

      if (previousAsset && previousAsset.files) {
        previousAsset.files = previousAsset.files.filter(({ id }) => id !== fileId)
      }

      queryClient.setQueryData(assetsManagementKeys.details(assetId), previousAsset)
    } catch (e) {
      displayToast('File could not be removed', 'error')
    }
  }

  const handleFilesUpload = async (assetFileQuality: IAssetFileQuality[]) => {
    if (!assetData) return

    try {
      const { files, ...assetToUpdate } = assetData

      const newFileQualityIds: IAssetFileQuality[] = files ? files.map(fileToFileQuality) : []

      await updateAssetMutation({ ...assetToUpdate, file_quality_ids: [...newFileQualityIds, ...assetFileQuality] })

      queryClient.invalidateQueries({ queryKey: assetsManagementKeys.details(assetId) })
    } catch (e) {
      displayToast('File could not be uploaded', 'error')
    }
  }

  const handleQualityIdentifierChange = async (assetFileQuality: IAssetFileQuality) => {
    if (!assetData) return

    try {
      const { files, ...assetToUpdate } = assetData

      const newFileQualityIds: IAssetFileQuality[] = files ? files.map(fileToFileQuality) : []

      for (const el of newFileQualityIds) {
        if (el.file_id === assetFileQuality.file_id) {
          el.qualifier = assetFileQuality.qualifier
          break
        }
      }

      await updateAssetMutation({ ...assetToUpdate, file_quality_ids: newFileQualityIds })

      queryClient.invalidateQueries({ queryKey: assetsManagementKeys.details(assetId) })

      displayToast('Quality identifier successfully updated', 'success')
    } catch (e) {
      displayToast('Quality identifier could not be updated', 'error')
    }
  }

  const toggleModal = (isOpen: boolean) => {
    return () => setIsModalOpen(isOpen)
  }

  const handleEditAssetFailure = () => {
    setIsModalOpen(false)
    displayToast('Asset could not be updated', 'error')
  }

  const handleEditAssetSuccess = () => {
    queryClient.invalidateQueries({ queryKey: assetsManagementKeys.details(assetId) })
    displayToast('Asset successfully updated', 'success')
    setIsModalOpen(false)
  }

  return (
    <>
      <ViewTitle className="flex justify-space-between">
        <BackNavigation title="Assets" to="/dynamic-screens/assets-management" />
        <div className="flex align-center justify-center">Asset details</div>
        <div className="empty" />
      </ViewTitle>
      <Layout className="asset-details-view">
        {assetData ? (
          <>
            <AssetDetailsGeneralInformation
              createdBy={createdByData}
              expirationDate={assetData.expiration_date}
              onEditAsset={toggleModal(true)}
              status={assetData.status}
              tags={assetData.tags}
              title={assetData.name}
              type={assetData.file_type}
              updatedBy={updatedByByData}
            />
            <AssetDetailsFileManagement
              files={assetData.files || []}
              isEditable={assetData.status === ASSET_STATUS.DRAFT}
              onFilesUpload={handleFilesUpload}
              onQualityIdentifierChange={handleQualityIdentifierChange}
              onRemoveAllFiles={handleRemoveAllFiles}
              onRemoveSingleFile={handleRemoveSingleFile}
              withDropzone={assetData.status === ASSET_STATUS.DRAFT}
            />
            <EditAssetModal
              assetId={assetData.id}
              isOpen={isModalOpen}
              onClose={toggleModal(false)}
              onFailure={handleEditAssetFailure}
              onSuccess={handleEditAssetSuccess}
              withAssetUploader={false}
            />
          </>
        ) : null}
      </Layout>
    </>
  )
}

export default AssetDetail
